<script setup>
import { ref, onMounted } from 'vue';
import Editor from '@tinymce/tinymce-vue';

// State für Formulardaten
const loading = ref(false);
const error = ref(null);
const successMessage = ref(null);
const aboutContent = ref({
  band_description: '',
  band_image: '',
  members: [
    { name: '', role: '', bio: '' },
    { name: '', role: '', bio: '' }
  ]
});

// Temporäres Bild für die Vorschau
const imagePreview = ref('');
const selectedFile = ref(null);

// Mitglied hinzufügen
const addMember = () => {
  aboutContent.value.members.push({ name: '', role: '', bio: '' });
};

// Mitglied entfernen
const removeMember = (index) => {
  if (aboutContent.value.members.length > 1) {
    aboutContent.value.members.splice(index, 1);
  } else {
    error.value = 'Es muss mindestens ein Bandmitglied vorhanden sein.';
    setTimeout(() => {
      error.value = null;
    }, 3000);
  }
};

// Bildvorschau aktualisieren
const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  // Nur Bilder akzeptieren
  if (!file.type.match('image.*')) {
    error.value = 'Bitte wählen Sie ein Bild aus (JPEG, PNG, GIF).';
    return;
  }
  
  selectedFile.value = file;
  
  // Vorschau erstellen
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

// Formular absenden
const saveAbout = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    // Daten für die API vorbereiten
    const formData = {
      band_description: aboutContent.value.band_description,
      band_description_html: true, // Markieren, dass der Text HTML-Formatierung enthält
      band_image: aboutContent.value.band_image, // Vorhandenes Bild beibehalten
      members: aboutContent.value.members
    };
    
    // Wenn ein neues Bild hochgeladen wurde, füge es den Daten hinzu
    if (imagePreview.value) {
      formData.band_image_upload = imagePreview.value;
    }
    
    const response = await fetch('/api/about.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });
    
    const data = await response.json();
    
    if (data.success) {
      successMessage.value = 'Die Informationen wurden erfolgreich gespeichert.';
      setTimeout(() => {
        successMessage.value = null;
      }, 5000);
      
      // Feld zurücksetzen
      selectedFile.value = null;
    } else {
      throw new Error(data.error || 'Ein Fehler ist aufgetreten.');
    }
  } catch (err) {
    console.error('Fehler beim Speichern der About-Daten:', err);
    error.value = err.message || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
  } finally {
    loading.value = false;
  }
};

// Inhalte laden
const loadAboutContent = async () => {
  try {
    loading.value = true;
    
    const response = await fetch('/api/about.php');
    const data = await response.json();
    
    if (data.success && data.about) {
      // Daten in das Formular laden
      aboutContent.value.band_description = data.about.band_description || '';
      aboutContent.value.band_image = data.about.band_image || '';
      
      // Wenn Mitglieder vorhanden sind, übernehme sie
      if (data.about.members && data.about.members.length > 0) {
        aboutContent.value.members = data.about.members;
      }
    }
  } catch (err) {
    console.error('Fehler beim Laden der About-Daten:', err);
    error.value = 'Die Inhalte konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

// Bei Komponentenaufbau Inhalte laden
onMounted(() => {
  loadAboutContent();
});

// TinyMCE-Konfiguration
const editorConfig = {
  plugins: 'link lists image table code help wordcount',
  toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | link image | removeformat',
  height: 300,
  menubar: 'file edit view insert format tools table help',
  language: 'de', // Deutsche Spracheinstellung
  language_url: 'https://cdn.jsdelivr.net/npm/tinymce-i18n@23.7.24/langs5/de.min.js', // Deutsche Sprache über CDN laden
  branding: false, // TinyMCE-Branding entfernen
  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
};
</script>

<template>
  <div class="about-manager">
    <div class="section-header">
      <h2>Über uns</h2>
      <p class="section-description">
        Verwalten Sie hier die Informationen über die Band und ihre Mitglieder.
      </p>
    </div>
    
    <form @submit.prevent="saveAbout" class="about-form">
      <!-- Bandbeschreibung -->
      <div class="form-card">
        <h3>Bandbeschreibung</h3>
        <div class="form-group">
          <label for="band-description">Beschreibungstext</label>
          <Editor
            api-key="1t9kxhlnvxnradg7zg6anbasvijrjtrr2k12wr0aqab5av8y"
            id="band-description"
            v-model="aboutContent.band_description"
            :init="editorConfig"
            cloudChannel="6" 
          />
          <p class="help-text">Nutzen Sie die Formatierungswerkzeuge für eine ansprechende Darstellung.</p>
        </div>
      </div>
      
      <!-- Bandbild -->
      <div class="form-card">
        <h3>Bandbild</h3>
        <div class="form-group">
          <div class="image-preview-container">
            <img 
              :src="imagePreview || aboutContent.band_image || '/placeholder.jpg'" 
              alt="Bandbild-Vorschau" 
              class="image-preview"
            >
          </div>
          
          <div class="file-input-wrapper">
            <label for="band-image" class="file-label">Bild auswählen</label>
            <input 
              type="file" 
              id="band-image" 
              @change="handleFileChange" 
              accept="image/*" 
              class="file-input"
            >
            <span class="selected-file">{{ selectedFile ? selectedFile.name : 'Keine Datei ausgewählt' }}</span>
          </div>
        </div>
      </div>
      
      <!-- Bandmitglieder -->
      <div class="form-card">
        <div class="card-header-with-actions">
          <h3>Bandmitglieder</h3>
          <button type="button" class="btn-add" @click="addMember">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Mitglied hinzufügen
          </button>
        </div>
        
        <div 
          v-for="(member, index) in aboutContent.members" 
          :key="index"
          class="member-card"
        >
          <div class="member-header">
            <h4>Mitglied {{ index + 1 }}</h4>
            <button type="button" class="btn-remove" @click="removeMember(index)" title="Entfernen">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label :for="`member-name-${index}`">Name</label>
              <input 
                :id="`member-name-${index}`" 
                v-model="member.name" 
                type="text" 
                placeholder="Name des Bandmitglieds"
                class="form-control"
              >
            </div>
            
            <div class="form-group">
              <label :for="`member-role-${index}`">Rolle</label>
              <input 
                :id="`member-role-${index}`" 
                v-model="member.role" 
                type="text" 
                placeholder="z.B. Gesang, Gitarre"
                class="form-control"
              >
            </div>
          </div>
          
          <div class="form-group">
            <label :for="`member-bio-${index}`">Biografie</label>
            <textarea 
              :id="`member-bio-${index}`" 
              v-model="member.bio" 
              rows="4"
              placeholder="Kurze Biografie des Bandmitglieds..."
              class="form-control"
            ></textarea>
          </div>
        </div>
      </div>
      
      <!-- Formular-Aktionen -->
      <div class="form-actions">
        <button type="submit" class="btn-save" :disabled="loading">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          Änderungen speichern
        </button>
      </div>
    </form>
    
    <!-- Nachrichten -->
    <div v-if="loading" class="loading-spinner">
      <div class="spinner"></div>
      <span>Wird geladen...</span>
    </div>
    
    <div v-if="error" class="error-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ error }}</span>
    </div>
    
    <div v-if="successMessage" class="success-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
  </div>
</template>

<style scoped>
.about-manager {
  max-width: 800px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.section-description {
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.5;
}

.about-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-card {
  background-color: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.form-card h3 {
  font-size: 1.25rem;
  margin-bottom: 1.25rem;
  color: var(--text-color);
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

.help-text {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
}

.image-preview-container {
  margin-bottom: 1rem;
  border-radius: 4px;
  overflow: hidden;
  max-width: 100%;
  max-height: 300px;
}

.image-preview {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.file-input-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.file-label {
  display: inline-block;
  padding: 0.65rem 1.25rem;
  background-color: var(--primary-color);
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.file-label:hover {
  background-color: var(--primary-color-dark);
}

.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
}

.selected-file {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.card-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card-header-with-actions h3 {
  margin: 0;
  padding: 0;
  border: none;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-add:hover {
  background-color: var(--primary-color-dark);
}

.member-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background-color: rgba(0, 0, 0, 0.02);
}

.member-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.member-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--text-color);
}

.btn-remove {
  background: none;
  border: none;
  color: #e53935;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.btn-remove:hover {
  color: #c62828;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-normal);
}

.btn-save:hover {
  background-color: var(--primary-color-dark);
}

.loading-spinner {
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-muted);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message, .success-message {
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 4px;
}

.error-message {
  background-color: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
}

.success-message {
  background-color: rgba(76, 175, 80, 0.1);
  color: #388e3c;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Editor-Anpassungen */
:deep(.tox-tinymce) {
  border-radius: 4px;
  border-color: #ced4da;
}

/* Die folgenden Stile sorgen dafür, dass der Editor zum Rest des Designs passt */
:deep(.tox-editor-container) {
  background-color: #fff;
}

:deep(.tox-toolbar) {
  background-color: #f8f9fa;
}
</style> 